import { SB } from '@play-co/replicant';
import createScheduledActions, {
  extractScheduledActionHandlers,
} from '../config/scheduledActions/createScheduledActions';
import { getMatch2TimedRewardTimestampKey } from './rewards/match2-rewards.getters';
import { Match2TimedRewardType } from './rewards/match2-rewards.ruleset';
import { isMatch2TimedRewardActive } from './match2.getters';
import { sendAnalyticsEvents } from '../analytics';
import { getTimedRewardUserProperties } from './match2.analytics';

const schema = {
  expireTimedReward: SB.object({ timedRewardType: SB.string() }),
};

const actions = createScheduledActions(schema)({
  expireTimedReward: async (state, args: { timedRewardType: string }, api) => {
    const timedRewardType = args.timedRewardType as Match2TimedRewardType;

    // Sanity check if reward is still active - skip expiration if still active
    // This can happen if the reward time is extended but the action is already being executed by the backend.
    // In this case we can simply return, since a new action will be scheduled.
    if (isMatch2TimedRewardActive(state, timedRewardType, api.date.now())) {
      return;
    }

    // Retrieve the cooldown key used in state
    const cooldownKey = getMatch2TimedRewardTimestampKey(timedRewardType);

    // Do nothing if not registered
    if (!state.match2.ongoingTimedRewards?.[cooldownKey]) {
      return;
    }

    // Do nothing if the reward is already expired
    if (state.match2.ongoingTimedRewards[cooldownKey].expired) {
      return;
    }

    // Sanity check if timestamps are defined
    if (!state.match2.timestamps) {
      throw new Error('Match2 timestamps is not defined');
    }

    // Find out the total duration of the reward
    const rewardDuration =
      state.match2.timestamps[cooldownKey] -
      state.match2.ongoingTimedRewards[cooldownKey].startTime;

    // Find out the total puzzles completed during the reward
    const puzzlesCompleted =
      state.match2.level -
      state.match2.ongoingTimedRewards[cooldownKey].levelStarted;

    // Send analytics event
    sendAnalyticsEvents(api, [
      {
        eventType: 'InfinteRewardEnd',
        userId: state.id,
        eventProperties: {
          reward: timedRewardType,
          rewardDuration,
          puzzlesCompleted,
        },
        userProperties: getTimedRewardUserProperties(state, api.date.now()),
      },
    ]);

    // Mark the reward as expired
    state.match2.ongoingTimedRewards[cooldownKey].expired = true;
  },
});

export const match2ScheduledActions = {
  schema,
  actions: extractScheduledActionHandlers(actions),
};
